import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';

const toolName = 'get-html-from-selected-element' as const;

const argumentZod = z.object({});

export const getHtmlFromSelectedElementTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Check elements',
  prettyStatus: {
    executing: 'Checking elements...',
    executed: 'Checked elements',
    failed: 'Failed to check elements',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Returns the HTML of the section surrounding the currently selected element in the Designer. Use when the user's request is vague ("change this", "fix that") and you need context about what they're looking at.

      Reuse this result only while no page-mutating tool or external action has changed the page. After a successful mutation or STALE_CONTEXT response, re-read before using element IDs or markup from an earlier result.

      Returns data-w-id attributes usable by render and other element tools. Use read-page instead when you need the full page.

      **COMPONENT INSTANCES (only applies when \`<wf-component>\` tags appear in the output):**
      If this tool's output contains \`<wf-component>\` elements, those are Webflow Component instances.
      They CANNOT be edited with set-element-text-content or any element-level tool. Components are edited through their props only.
      - If the user asks to change text that is inside a \`<wf-component>\`, check the component's \`prop-*\` attributes.
      - If a matching prop exists (e.g. \`prop-title\`, \`prop-subtitle\`), use the render tool with MODIFY mode to update the prop.
      - If NO matching prop exists for the requested content, tell the user the content cannot be edited because it is inside a component that does not expose a prop for it.
      - NEVER use set-element-text-content on elements inside a \`<wf-component>\`. It will fail.
      - If no \`<wf-component>\` tags appear in the output, ignore this section.

      **SLOTS (only applies when \`<wf-slot>\` tags appear in the output):**
      A \`<wf-slot>\` is a container inside a component instance that can hold component instances or plain HTML. Slots have NO props of their own.
      - To edit content inside a slot, target the individual \`<wf-component>\` children inside the \`<wf-slot>\` using their \`data-w-id\` with the render tool's MODIFY mode.
      - To add a new component instance into a slot, use the render tool with INSERT mode (\`<wf-component component-id="...">\`) targeting the slot or an existing child as the anchor. Plain HTML/text can also be inserted directly.
      - To reorder \`<wf-component>\` instances inside a slot, prefer \`page-automation-public-mcp-compound-elements\` action \`move_element\` (keeps bindings/styles). If using render, rewrite the slot children in one call with PRESERVE (\`<wf-component data-w-id="...">\`) in the new order while instances are still on the page — never \`remove\` first, and never rebuild as freeform HTML.
      - NEVER attempt to set props on the \`<wf-slot>\` element itself — it is purely a container.
      - If the user says "change this" while a slot is selected, look at the \`<wf-component>\` children inside the slot to identify what to modify.
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  permission: [{resource: 'SitePage', action: 'editContent'}],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => true,
  isReadOnly: () => true,
  timeout: 30000, // 30 seconds for removing style property
  compaction: {
    prompt:
      'Summarize the selected element HTML. Preserve element data-w-ids, CSS class names, text content, CMS collection IDs, and structural relationships. Remove raw HTML tags and repetitive markup.',
    staleAfterTurns: 3,
  },
});
